import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Calendar,
  Loader2,
  Printer,
  Layers,
  Clock,
  TrendingUp,
  Receipt,
  Wallet,
  CreditCard,
  Calculator,
  Scale,
} from 'lucide-react';
import clsx from 'clsx';
import { shiftApi } from '@/services/api';
import type { DayZReport, POSShift } from '@/types';
import { browserPrintDayZReport } from '@/utils/shiftReceiptPrint';
import { formatMoney } from '@/utils/currency';
import { downloadCsv } from '@/utils/csv';
import ReportExportButton from '@/components/reports/ReportExportButton';

export default function ZDayReportSection() {
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedShiftId, setSelectedShiftId] = useState<string | 'all'>('all');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['dayZReport', date],
    queryFn: async () => {
      const res = await shiftApi.getDayZReport(date);
      return res.data.message as DayZReport;
    },
  });

  const handleExport = () => {
    if (!data?.shifts?.length) return;
    downloadCsv(
      `Z_Day_${date}.csv`,
      [
        { key: 'z_number', label: 'Z#' },
        { key: 'name', label: 'Shift' },
        { key: 'cashier_name', label: 'Cashier' },
        { key: 'pocket_name', label: 'Drawer' },
        { key: 'invoice_count', label: 'Invoices' },
        { key: 'total_sales', label: 'Total Sales' },
        { key: 'cash_sales', label: 'Cash Sales' },
        { key: 'bank_sales', label: 'Bank Sales' },
        { key: 'cash_returns', label: 'Returns' },
        { key: 'payments_collected', label: 'Cash Col.' },
        { key: 'payments_collected_bank', label: 'Bank Col.' },
        { key: 'bank_returns', label: 'Bank Ret.' },
        { key: 'credit_returns', label: 'Credit Ret.' },
        { key: 'paid_in', label: 'Paid In' },
        { key: 'paid_out', label: 'Paid Out' },
        { key: 'drops', label: 'Drops' },
        { key: 'total_drawer_expenses', label: 'Expenses' },
        { key: 'opening_amount', label: 'Opening' },
        { key: 'expected_cash', label: 'Expected' },
        { key: 'closing_amount', label: 'Counted' },
        { key: 'difference', label: 'Variance' },
      ],
      data.shifts.map((s) => {
        const bankSales = s.non_cash_breakdown
          ? Object.values(s.non_cash_breakdown).reduce((acc: number, val: any) => acc + (Number(val) || 0), 0)
          : Math.max(0, (s.total_sales || 0) - (s.cash_sales || 0));
        return {
          ...s,
          cashier_name: s.cashier_name || s.cashier,
          bank_sales: bankSales,
        };
      }),
    );
  };

  const selectedShift: POSShift | undefined =
    selectedShiftId === 'all'
      ? undefined
      : data?.shifts?.find((s) => s.name === selectedShiftId);

  const activeSummary = selectedShift || data;
  const isShiftSelected = Boolean(selectedShift);

  const bankSales = activeSummary?.non_cash_breakdown
    ? Object.values(activeSummary.non_cash_breakdown).reduce(
        (acc: number, val: any) => acc + (Number(val) || 0),
        0,
      )
    : Math.max(0, (activeSummary?.total_sales || 0) - (activeSummary?.cash_sales || 0));
  const bankTotal = bankSales + (activeSummary?.payments_collected_bank || 0);
  const totalCashIn = (activeSummary?.cash_sales || 0) + (activeSummary?.payments_collected || 0);
  const variance = activeSummary?.difference || 0;

  return (
    <div className="space-y-4">
      {/* Top Action Bar */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-400" />
          <input
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setSelectedShiftId('all');
            }}
            className="border-gray-200 rounded-lg text-sm"
          />
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-sm px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>
        <div className="flex gap-2">
          <ReportExportButton onClick={handleExport} disabled={!data?.shifts?.length || isLoading} />
          <button
            type="button"
            disabled={!data}
            onClick={() => data && browserPrintDayZReport(data)}
            className="text-sm px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40 inline-flex items-center gap-2"
          >
            <Printer className="w-4 h-4" /> Print Day Z
          </button>
        </div>
      </div>

      {isLoading && (
        <p className="text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading day Z…
        </p>
      )}

      {isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {(error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to load day Z report'}
        </div>
      )}

      {data && activeSummary && (
        <>
          {/* Shift Selector Tabs */}
          {data.shifts && data.shifts.length > 0 && (
            <div className="bg-white p-2 rounded-xl border border-gray-100 shadow-sm flex items-center gap-2 overflow-x-auto">
              <button
                type="button"
                onClick={() => setSelectedShiftId('all')}
                className={clsx(
                  'px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-2 whitespace-nowrap',
                  selectedShiftId === 'all'
                    ? 'bg-gray-900 text-white shadow-sm'
                    : 'bg-gray-50 text-gray-600 hover:text-gray-900 hover:bg-gray-100 border border-transparent',
                )}
              >
                <Layers className="w-3.5 h-3.5" />
                <span>All Shifts (Day Total)</span>
                <span
                  className={clsx(
                    'px-1.5 py-0.5 rounded-full text-[10px] font-bold',
                    selectedShiftId === 'all' ? 'bg-gray-800 text-gray-200' : 'bg-gray-200 text-gray-700',
                  )}
                >
                  {data.shifts.length}
                </span>
              </button>

              <div className="h-5 w-px bg-gray-200 mx-0.5 shrink-0" />

              {data.shifts.map((s, idx) => {
                const isSelected = selectedShiftId === s.name;
                const shiftNum = s.z_number ? `Z#${s.z_number}` : `Shift #${idx + 1}`;
                const cashier = s.cashier_name || s.cashier || 'Cashier';
                const hasVariance = (s.difference || 0) !== 0;

                return (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => setSelectedShiftId(s.name)}
                    className={clsx(
                      'px-3 py-1.5 text-xs rounded-lg transition-all flex items-center gap-2 whitespace-nowrap border',
                      isSelected
                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm font-semibold'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200 font-medium',
                    )}
                  >
                    <Clock className={clsx('w-3.5 h-3.5', isSelected ? 'text-white' : 'text-gray-400')} />
                    <span>{shiftNum}</span>
                    <span
                      className={clsx(
                        'text-[11px] truncate max-w-[130px]',
                        isSelected ? 'text-blue-100' : 'text-gray-500',
                      )}
                    >
                      {cashier}
                      {s.pocket_name ? ` · ${s.pocket_name}` : ''}
                    </span>
                    {hasVariance && (
                      <span
                        className={clsx(
                          'w-2 h-2 rounded-full shrink-0',
                          isSelected ? 'bg-amber-300' : 'bg-amber-500',
                        )}
                        title={`Variance: ${formatMoney(s.difference || 0)}`}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Compact Horizontal KPI Stats Ribbon */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {/* Sales */}
            <div className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
              <div className="flex items-center justify-between text-gray-500">
                <span className="text-xs font-medium">Total Sales</span>
                <div className="p-1.5 bg-emerald-50 rounded-lg text-emerald-600">
                  <TrendingUp className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="mt-2">
                <p className="text-lg font-bold text-gray-900 leading-tight">
                  {formatMoney(activeSummary.total_sales || 0)}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {isShiftSelected ? 'Selected Shift' : 'All Shifts Total'}
                </p>
              </div>
            </div>

            {/* Invoices */}
            <div className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
              <div className="flex items-center justify-between text-gray-500">
                <span className="text-xs font-medium">Invoices</span>
                <div className="p-1.5 bg-blue-50 rounded-lg text-blue-600">
                  <Receipt className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="mt-2">
                <p className="text-lg font-bold text-gray-900 leading-tight">
                  {activeSummary.invoice_count ?? 0}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {isShiftSelected
                    ? 'Shift Invoices'
                    : `${data.shift_count || 0} Shift${data.shift_count === 1 ? '' : 's'}`}
                </p>
              </div>
            </div>

            {/* Cash Received */}
            <div className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
              <div className="flex items-center justify-between text-gray-500">
                <span className="text-xs font-medium">Cash Received</span>
                <div className="p-1.5 bg-green-50 rounded-lg text-green-600">
                  <Wallet className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="mt-2">
                <p className="text-lg font-bold text-gray-900 leading-tight">
                  {formatMoney(totalCashIn)}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                  {activeSummary.payments_collected
                    ? `Incl. ${formatMoney(activeSummary.payments_collected)} col.`
                    : 'Sales in Cash'}
                </p>
              </div>
            </div>

            {/* Bank & Digital */}
            <div className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
              <div className="flex items-center justify-between text-gray-500">
                <span className="text-xs font-medium">Bank & Digital</span>
                <div className="p-1.5 bg-purple-50 rounded-lg text-purple-600">
                  <CreditCard className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="mt-2">
                <p className="text-lg font-bold text-gray-900 leading-tight">
                  {formatMoney(bankTotal)}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                  {activeSummary.payments_collected_bank
                    ? `Incl. ${formatMoney(activeSummary.payments_collected_bank)} col.`
                    : 'Cards & Online'}
                </p>
              </div>
            </div>

            {/* Expected Cash */}
            <div className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
              <div className="flex items-center justify-between text-gray-500">
                <span className="text-xs font-medium">Expected Cash</span>
                <div className="p-1.5 bg-indigo-50 rounded-lg text-indigo-600">
                  <Calculator className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="mt-2">
                <p className="text-lg font-bold text-gray-900 leading-tight">
                  {formatMoney(activeSummary.expected_cash || 0)}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {isShiftSelected && activeSummary.closing_amount !== undefined
                    ? `Counted: ${formatMoney(activeSummary.closing_amount)}`
                    : 'Drawer Expected'}
                </p>
              </div>
            </div>

            {/* Variance */}
            <div className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
              <div className="flex items-center justify-between text-gray-500">
                <span className="text-xs font-medium">Variance</span>
                <div
                  className={clsx(
                    'p-1.5 rounded-lg',
                    variance === 0
                      ? 'bg-emerald-50 text-emerald-600'
                      : variance < 0
                        ? 'bg-red-50 text-red-600'
                        : 'bg-amber-50 text-amber-600',
                  )}
                >
                  <Scale className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="mt-2">
                <p
                  className={clsx(
                    'text-lg font-bold leading-tight',
                    variance === 0
                      ? 'text-emerald-600'
                      : variance < 0
                        ? 'text-red-600'
                        : 'text-amber-600',
                  )}
                >
                  {formatMoney(variance)}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {variance === 0 ? 'Balanced' : variance > 0 ? 'Surplus (+)' : 'Shortage (-)'}
                </p>
              </div>
            </div>
          </div>

          {/* Collapsible Detail Cards - commented out for now as requested
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900">
                  {selectedShift
                    ? `Shift Details: ${selectedShift.cashier_name || selectedShift.cashier} (${selectedShift.z_number ? `Z#${selectedShift.z_number}` : selectedShift.pocket_name})`
                    : 'Detailed Day Breakdown'}
                </h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                  {selectedShift ? selectedShift.pocket_name : `${data.shift_count} Shifts Total`}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setDetailsExpanded((prev) => !prev)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                {detailsExpanded ? (
                  <>
                    <ChevronUp className="w-3.5 h-3.5" />
                    <span>Hide Details</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3.5 h-3.5" />
                    <span>Show Details</span>
                  </>
                )}
              </button>
            </div>

            {detailsExpanded && (
              <ShiftSummaryCards
                summary={activeSummary}
                openingAmount={activeSummary.opening_amount}
                layout="grid"
              />
            )}
          </div>
          */}

          {/* Shifts Table with Grouped Column Headers */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">
                  Shift Records & Breakdown ({data.shifts?.length || 0})
                </span>
                <span className="text-[11px] text-gray-400">
                  Detailed metrics grouped by Shift, Sales, Collections & Returns, Cash Movements, Drawer & Variance
                </span>
              </div>
              {selectedShiftId !== 'all' && (
                <button
                  type="button"
                  onClick={() => setSelectedShiftId('all')}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear Selection
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs text-left border-collapse">
                <thead>
                  {/* Category Grouping Row */}
                  <tr className="bg-gray-100/80 text-[11px] font-bold text-gray-600 uppercase tracking-wider border-b border-gray-200">
                    <th colSpan={4} className="px-3 py-2 text-left border-r border-gray-200 bg-gray-100">
                      Shift Info
                    </th>
                    <th colSpan={4} className="px-3 py-2 text-center border-r border-gray-200 bg-blue-50/60 text-blue-900">
                      Sales Summary
                    </th>
                    <th colSpan={4} className="px-3 py-2 text-center border-r border-gray-200 bg-indigo-50/60 text-indigo-900">
                      Collections & Returns
                    </th>
                    <th colSpan={4} className="px-3 py-2 text-center border-r border-gray-200 bg-amber-50/60 text-amber-900">
                      Cash Movements
                    </th>
                    <th colSpan={3} className="px-3 py-2 text-center bg-emerald-50/60 text-emerald-900">
                      Drawer & Reconciliation
                    </th>
                  </tr>
                  {/* Sub-column Row */}
                  <tr className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200 text-[11px]">
                    {/* Shift Info */}
                    <th className="px-2.5 py-2">Z#</th>
                    <th className="px-2.5 py-2">Cashier</th>
                    <th className="px-2.5 py-2">Drawer</th>
                    <th className="px-2.5 py-2 border-r border-gray-200">Invoices</th>

                    {/* Sales Summary */}
                    <th className="px-2.5 py-2 text-right">Total Sales</th>
                    <th className="px-2.5 py-2 text-right">Cash Sales</th>
                    <th className="px-2.5 py-2 text-right">Bank Sales</th>
                    <th className="px-2.5 py-2 text-right border-r border-gray-200">Returns</th>

                    {/* Collections & Returns */}
                    <th className="px-2.5 py-2 text-right">Cash Col. (+)</th>
                    <th className="px-2.5 py-2 text-right">Bank Col.</th>
                    <th className="px-2.5 py-2 text-right">Bank Ret.</th>
                    <th className="px-2.5 py-2 text-right border-r border-gray-200">Credit Ret.</th>

                    {/* Cash Movements */}
                    <th className="px-2.5 py-2 text-right">Paid In (+)</th>
                    <th className="px-2.5 py-2 text-right">Paid Out (-)</th>
                    <th className="px-2.5 py-2 text-right">Drops (-)</th>
                    <th className="px-2.5 py-2 text-right border-r border-gray-200">Expenses (-)</th>

                    {/* Drawer & Reconciliation */}
                    <th className="px-2.5 py-2 text-right">Opening</th>
                    <th className="px-2.5 py-2 text-right">Expected</th>
                    <th className="px-2.5 py-2 text-right">Counted</th>
                    <th className="px-2.5 py-2 text-right">Variance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(data.shifts || []).map((s) => {
                    const isSelected = selectedShiftId === s.name;
                    const shiftBankSales = s.non_cash_breakdown
                      ? Object.values(s.non_cash_breakdown).reduce(
                          (acc: number, val: any) => acc + (Number(val) || 0),
                          0,
                        )
                      : Math.max(0, (s.total_sales || 0) - (s.cash_sales || 0));

                    return (
                      <tr
                        key={s.name}
                        onClick={() => setSelectedShiftId((prev) => (prev === s.name ? 'all' : s.name))}
                        className={clsx(
                          'cursor-pointer transition-colors',
                          isSelected ? 'bg-blue-50/80 font-medium' : 'hover:bg-gray-50/80',
                        )}
                      >
                        {/* Shift Info */}
                        <td className="px-2.5 py-2">
                          <span
                            className={clsx(
                              'px-1.5 py-0.5 rounded text-[10px] font-bold',
                              isSelected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700',
                            )}
                          >
                            {s.z_number ? `Z#${s.z_number}` : '—'}
                          </span>
                        </td>
                        <td className="px-2.5 py-2 text-gray-900 truncate max-w-[110px]" title={s.cashier_name || s.cashier}>
                          {s.cashier_name || s.cashier}
                        </td>
                        <td className="px-2.5 py-2 text-gray-600 truncate max-w-[100px]" title={s.pocket_name}>
                          {s.pocket_name}
                        </td>
                        <td className="px-2.5 py-2 text-center text-gray-700 border-r border-gray-200">
                          {s.invoice_count ?? 0}
                        </td>

                        {/* Sales Summary */}
                        <td className="px-2.5 py-2 text-right font-medium text-gray-900">
                          {formatMoney(s.total_sales || 0)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-gray-700">
                          {formatMoney(s.cash_sales || 0)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-gray-700">
                          {formatMoney(shiftBankSales)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-amber-700 border-r border-gray-200">
                          {formatMoney(s.cash_returns || 0)}
                        </td>

                        {/* Collections & Returns */}
                        <td className="px-2.5 py-2 text-right text-blue-700 font-medium">
                          {formatMoney(s.payments_collected || 0)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-blue-700">
                          {formatMoney(s.payments_collected_bank || 0)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-amber-700">
                          {formatMoney(s.bank_returns || 0)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-purple-700 border-r border-gray-200">
                          {formatMoney((s as any).credit_returns || 0)}
                        </td>

                        {/* Cash Movements */}
                        <td className="px-2.5 py-2 text-right text-emerald-700">
                          {formatMoney(s.paid_in || 0)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-orange-700">
                          {formatMoney(s.paid_out || 0)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-red-600">
                          {formatMoney(s.drops || 0)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-red-600 border-r border-gray-200">
                          {formatMoney(s.total_drawer_expenses || 0)}
                        </td>

                        {/* Drawer & Reconciliation */}
                        <td className="px-2.5 py-2 text-right text-gray-600">
                          {formatMoney(s.opening_amount || 0)}
                        </td>
                        <td className="px-2.5 py-2 text-right font-medium text-gray-900">
                          {formatMoney(s.expected_cash || 0)}
                        </td>
                        <td className="px-2.5 py-2 text-right font-medium text-gray-900">
                          {formatMoney(s.closing_amount || 0)}
                        </td>
                        <td className="px-2.5 py-2 text-right">
                          <span
                            className={clsx(
                              'font-bold px-1.5 py-0.5 rounded text-[11px]',
                              (s.difference || 0) === 0
                                ? 'text-emerald-700 bg-emerald-50'
                                : (s.difference || 0) < 0
                                  ? 'text-red-700 bg-red-50'
                                  : 'text-amber-700 bg-amber-50',
                            )}
                          >
                            {formatMoney(s.difference || 0)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {!data.shifts?.length && (
                    <tr>
                      <td colSpan={19} className="px-3 py-8 text-center text-gray-400">
                        No closed shifts for this date
                      </td>
                    </tr>
                  )}
                </tbody>
                {/* Total Summary Row for All Shifts */}
                {data.shifts && data.shifts.length > 0 && (
                  <tfoot className="bg-gray-50 border-t-2 border-gray-300 font-semibold text-gray-900 text-xs">
                    <tr>
                      <td colSpan={3} className="px-2.5 py-2.5 text-left text-gray-700">
                        Day Total ({data.shifts.length} shifts)
                      </td>
                      <td className="px-2.5 py-2.5 text-center border-r border-gray-200">
                        {data.invoice_count ?? 0}
                      </td>

                      {/* Sales Totals */}
                      <td className="px-2.5 py-2.5 text-right font-bold text-gray-900">
                        {formatMoney(data.total_sales || 0)}
                      </td>
                      <td className="px-2.5 py-2.5 text-right text-gray-800">
                        {formatMoney(data.cash_sales || 0)}
                      </td>
                      <td className="px-2.5 py-2.5 text-right text-gray-800">
                        {formatMoney(
                          data.non_cash_breakdown
                            ? Object.values(data.non_cash_breakdown).reduce((a, b) => a + (Number(b) || 0), 0)
                            : Math.max(0, (data.total_sales || 0) - (data.cash_sales || 0)),
                        )}
                      </td>
                      <td className="px-2.5 py-2.5 text-right text-amber-800 border-r border-gray-200">
                        {formatMoney(data.cash_returns || 0)}
                      </td>

                      {/* Collections & Returns Totals */}
                      <td className="px-2.5 py-2.5 text-right text-blue-800 font-bold">
                        {formatMoney(data.payments_collected || 0)}
                      </td>
                      <td className="px-2.5 py-2.5 text-right text-blue-800">
                        {formatMoney(data.payments_collected_bank || 0)}
                      </td>
                      <td className="px-2.5 py-2.5 text-right text-amber-800">
                        {formatMoney(data.bank_returns || 0)}
                      </td>
                      <td className="px-2.5 py-2.5 text-right text-purple-800 border-r border-gray-200">
                        {formatMoney((data as any).credit_returns || 0)}
                      </td>

                      {/* Cash Movements Totals */}
                      <td className="px-2.5 py-2.5 text-right text-emerald-800">
                        {formatMoney(data.paid_in || 0)}
                      </td>
                      <td className="px-2.5 py-2.5 text-right text-orange-800">
                        {formatMoney(data.paid_out || 0)}
                      </td>
                      <td className="px-2.5 py-2.5 text-right text-red-700">
                        {formatMoney(data.drops || 0)}
                      </td>
                      <td className="px-2.5 py-2.5 text-right text-red-700 border-r border-gray-200">
                        {formatMoney(data.total_drawer_expenses || 0)}
                      </td>

                      {/* Drawer & Reconciliation Totals */}
                      <td className="px-2.5 py-2.5 text-right text-gray-700">
                        {formatMoney(data.opening_amount || 0)}
                      </td>
                      <td className="px-2.5 py-2.5 text-right font-bold text-gray-900">
                        {formatMoney(data.expected_cash || 0)}
                      </td>
                      <td className="px-2.5 py-2.5 text-right font-bold text-gray-900">
                        {formatMoney(data.closing_amount || 0)}
                      </td>
                      <td className="px-2.5 py-2.5 text-right">
                        <span
                          className={clsx(
                            'font-bold px-1.5 py-0.5 rounded text-[11px]',
                            (data.difference || 0) === 0
                              ? 'text-emerald-700 bg-emerald-50'
                              : (data.difference || 0) < 0
                                ? 'text-red-700 bg-red-50'
                                : 'text-amber-700 bg-amber-50',
                          )}
                        >
                          {formatMoney(data.difference || 0)}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
